import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { supportedCities } from '../data/supportedCities';
import { BrandLockup } from './BrandLogo';

const GENDERS = ['male', 'female', 'non-binary', 'other'];

export const GoogleCompleteProfile = ({ onboarding, onCancel }) => {
  const { completeGoogleProfile } = useAuth();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: onboarding.name || '', birthday: '', gender: '', looking_for: [], location: supportedCities[0].name, bio: '' });
  const [loading, setLoading] = useState(false);

  const toggleLooking = (g) => setForm((f) => ({ ...f, looking_for: f.looking_for.includes(g) ? f.looking_for.filter((x) => x !== g) : [...f.looking_for, g] }));

  const submit = async (e) => {
    e.preventDefault();
    const dob = new Date(form.birthday);
    const age = Math.floor((Date.now() - dob.getTime()) / (365.25 * 24 * 3600 * 1000));
    if (!form.birthday || Number.isNaN(dob.getTime()) || age < 18) {
      toast({ title: '18+ only', description: 'You must be at least 18 years old to join FlameLink.', variant: 'destructive' });
      return;
    }
    if (!form.gender || form.looking_for.length === 0) {
      toast({ title: 'Almost there', description: 'Please choose your gender and who you are looking for.', variant: 'destructive' });
      return;
    }
    setLoading(true);
    const result = await completeGoogleProfile({ ...form, birthday: dob.toISOString(), onboarding_token: onboarding.token });
    setLoading(false);
    if (result.success) toast({ title: 'Welcome to FlameLink!', description: 'Your account is ready.' });
    else toast({ title: 'Could not finish sign-up', description: result.error, variant: 'destructive' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-red-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8" data-testid="google-complete-profile">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center mb-3">
            <BrandLockup size="lg" testId="google-onboarding-brand" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Complete your profile</h2>
          <p className="text-sm text-gray-600 mt-1">Signed in with Google as <span className="font-medium" data-testid="google-onboarding-email">{onboarding.email}</span></p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
            <Input data-testid="google-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required maxLength={50} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Birthday</label>
            <Input data-testid="google-birthday-input" type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">I am</label>
            <select data-testid="google-gender-select" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500">
              <option value="">Select your gender</option>
              {GENDERS.map((g) => <option key={g} value={g} className="capitalize">{g.charAt(0).toUpperCase() + g.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Looking for</label>
            <div className="space-y-2">
              {GENDERS.map((g) => (
                <label key={g} className="flex items-center">
                  <input type="checkbox" data-testid={`google-looking-for-${g}`} checked={form.looking_for.includes(g)} onChange={() => toggleLooking(g)}
                    className="mr-2 h-4 w-4 text-red-500 border-gray-300 rounded focus:ring-red-500" />
                  <span className="text-sm text-gray-700 capitalize">{g}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
            <select data-testid="google-location-select" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500">
              {supportedCities.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Bio (Optional)</label>
            <Input data-testid="google-bio-input" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} maxLength={500} placeholder="Tell us about yourself..." />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-red-500 to-pink-500 text-white" data-testid="google-complete-submit-btn">
            {loading ? 'Creating your account…' : 'Finish sign-up'}
          </Button>
          <Button type="button" variant="ghost" className="w-full" onClick={onCancel} data-testid="google-complete-cancel-btn">Cancel</Button>
        </form>
      </Card>
    </div>
  );
};

export default GoogleCompleteProfile;
